/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { PropsWithChildren, useEffect, useState, useRef } from 'react';
import { Box, Stack, styled, Typography } from '@mui/material';

import { SalesAgreementPromoProductSelectionVariant } from './types';
import { Button } from '../../../../components/Button';
import TestIds from '../../../../testIds';
import Modal from '../../../../components/Modal';
import { formatPrice, generateImageUrl } from '../../../../util/helpers';
import PlaceholderImage from '../../../../assets/images/image-placeholder.png';
import ImageWithPlaceholder from '../../../../components/ImageWithPlaceholder';
import {
  PriceStyle,
  ProductPriceDetails,
} from '../../../products/components/ProductCard/ProductPriceDetails';
import PrintVariants from '../../../products/components/ProductCard/PrintVariants';
import {
  UnitPriceForm,
  IUnitPriceFormRef,
  IUnitPriceState,
  TUnitPriceFormData,
} from '../../../../components/forms/UnitPriceForm/UnitPriceForm';
import { isPriceDifferent } from '../../../../util/formatting';

export interface IEditedSAItemDetails {
  editedPrice: number;
  editedPriceReasonCode: string | undefined;
  editedPriceReasonNote: string | undefined;
  item: SalesAgreementPromoProductSelectionVariant;
}

export interface IEditSalesAgreementProductPriceModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  item?: SalesAgreementPromoProductSelectionVariant;
  onApplyEditedPrice: (editedSAItemDetails: IEditedSAItemDetails) => void;
  onRevertEditedPrice: (item: SalesAgreementPromoProductSelectionVariant) => void;
}

const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const EditSalesAgreementProductPriceModal = ({
  open,
  setOpen,
  item,
  onApplyEditedPrice,
  onRevertEditedPrice,
}: PropsWithChildren<IEditSalesAgreementProductPriceModalProps>): JSX.Element | null => {
  const unitPriceFormRef = useRef<IUnitPriceFormRef>(null);
  const [editedPrice, setEditedPrice] = useState<number | undefined>();

  useEffect(() => {
    if (item?.editedPrice) {
      setEditedPrice(item.editedPrice);
    } else {
      setEditedPrice(undefined);
    }
  }, [item, open]);

  const handleCloseModal = (): void => setOpen(false);

  const handleUnitPriceFormChange = (updatedUnitPriceState?: IUnitPriceState): void => {
    setEditedPrice(updatedUnitPriceState?.unitPrice);
  };

  const handleUnitPriceFormRevertChanges = (): void => {
    if (item) {
      setEditedPrice(undefined);
      onRevertEditedPrice(item);
    }

    handleCloseModal();
  };

  const applyEditedPrice = async (): Promise<void> => {
    await unitPriceFormRef.current?.submit();
  };

  const handleUnitPriceFormSubmit = (formData: TUnitPriceFormData): void => {
    const newPrice = parseFloat(formData.unitPrice || '0');
    const unitPriceChanged: boolean | undefined = isPriceDifferent(newPrice, item?.unitPrice || 0);
    const reasonChanged: boolean =
      !!(item?.lastEditedReasonCode && formData.reasonCode !== item?.lastEditedReasonCode) ||
      !!(item?.lastEditedReasonNote && formData.reasonNote !== item?.lastEditedReasonNote);
    const shouldUpdateReason: boolean = unitPriceChanged || reasonChanged;

    if (item && shouldUpdateReason) {
      onApplyEditedPrice({
        editedPrice: newPrice,
        editedPriceReasonCode: formData.reasonCode || undefined,
        editedPriceReasonNote: formData.reasonNote || undefined,
        item,
      });
    }

    handleCloseModal();
  };

  const variants = [
    ...(item?.sizeDescription ? [{ label: 'Size', value: item.sizeDescription }] : []),
    ...(item?.colourDescription ? [{ label: 'Colour', value: item.colourDescription }] : []),
  ];

  return item && open ? (
    <Modal
      title='Edit unit price'
      open={open}
      handleClose={handleCloseModal}
      footer={
        <StyledButton
          data-testid={TestIds.feat.editPrice.saveChanges}
          variant='contained'
          onClick={applyEditedPrice}
        >
          Save changes
        </StyledButton>
      }
      testId={TestIds.feat.editPrice.modal}
    >
      <Stack spacing={4} pb={2}>
        <Stack direction={'row'} gap={2}>
          <Box height={'80px'} width={'80px'}>
            <ImageWithPlaceholder
              src={generateImageUrl(item.itemNumber)}
              alt=''
              placeholderImageUrl={PlaceholderImage}
              size={160}
            />
          </Box>

          <Stack gap={1}>
            <Typography variant='h3'>{item.description}</Typography>

            <ProductPriceDetails
              items={[
                {
                  type: PriceStyle.Discounted,
                  label: 'Your price:',
                  gstLabel: 'GST excl.',
                  value: formatPrice(editedPrice || item.unitPrice),
                  testId: TestIds.feat.product.card.discountedPrice,
                },
                {
                  type: PriceStyle.RRP,
                  label: 'RRP:',
                  gstLabel: 'GST excl.',
                  value: formatPrice(item.unitPrice),
                  testId: TestIds.feat.product.card.nationalPrice,
                },
              ]}
              size='Medium'
              sx={{
                gap: 2,
              }}
            />

            <PrintVariants items={variants} direction='row' />
          </Stack>
        </Stack>

        <UnitPriceForm
          ref={unitPriceFormRef}
          productId={item.productId}
          unitPriceState={{
            unitPrice: item.editedPrice || item.unitPrice,
            reasonCode: item?.lastEditedReasonCode,
            reasonNote: item?.lastEditedReasonNote,
          }}
          validateAllFieldsOnSubmit={!!(item?.lastEditedReasonCode || item?.lastEditedReasonNote)}
          onFormChange={handleUnitPriceFormChange}
          onSubmit={handleUnitPriceFormSubmit}
          onRevertChanges={handleUnitPriceFormRevertChanges}
        />
      </Stack>
    </Modal>
  ) : null;
};
